import React from 'react';
import { Collapse, Switch, Tag } from 'antd';
import ListEditItem from '../../../shared/ListEditItem';
import ListItem from '../../../shared/ListItem';
import ImageAdded from '../../../TimeLine/ImageAdded';
import { getStatusColor } from '../../../../utils';
import { renderText } from '../../../shared/DrawerLayout/renderText';
import { renderDate } from '../../../shared/TableLayout';
import styles from '../UserInfo.styles';
import formatDate from '../../../../utils/formatDate';

const { Panel } = Collapse;

interface UserDLInfoProps {
  selectedUser: any;
  intl: any;
  onEditLicenseSettings: () => void;
  openAddLicenseModal: () => void;
  onDLStatusUpdate: () => void;
}

const UserDLInfo: React.FC<UserDLInfoProps> = ({
  selectedUser,
  intl,
  onEditLicenseSettings,
  openAddLicenseModal,
  onDLStatusUpdate,
}) => (
  <div key={selectedUser?.id}>
    <div data-testid="UserDLInfo-Drivinglicense" className="user-section-title">
      {intl.formatMessage({ id: `fleetadmin.users.info.drivinglicense` })}
      <img
        data-testid="UserDLInfo-updateDLData"
        className="add-button"
        src="/images/drawer/add.svg"
        alt="edit"
        onClick={openAddLicenseModal}
      />
    </div>
    <ListEditItem
      label={intl.formatMessage({ id: `fleetadmin.users.info.dl.submissions` })}
      leftItem={<span>{selectedUser?.remainingLicenseSubmissionAttempts}</span>}
      onClickEdit={onEditLicenseSettings}
    />
    <ListEditItem
      leftItem={
        <div className="permission-label">
          {intl.formatMessage({ id: `fleetadmin.users.info.dl.young` })}
        </div>
      }
      rightItem={
        <Switch checked={selectedUser?.youngDriverApproved} disabled />
      }
    />
    <div className="collapse-container">
      <Collapse data-testid="UserDLInfo-collapseItem" bordered={false}>
        {selectedUser?.licenses?.map((license: any, index: number) => (
          <Panel
            header={
              <div className="dl-header">
                {license?.submissionDate || license?.verificationDate ? (
                  <span>
                    {`${intl.formatMessage({
                      id: `fleetadmin.users.info.dl.submitted`,
                    })} ${formatDate(
                      license?.submissionDate || license?.verificationDate
                    )}`}
                  </span>
                ) : (
                  <span>
                    {`${intl.formatMessage({
                      id: `fleetadmin.date.not.available`,
                    })}`}
                  </span>
                )}

                {license?.licenseStatus && (
                  <Tag
                    data-testid={`UserDLInfo-${license?.licenseStatus}`}
                    color={getStatusColor(license.licenseStatus)}
                  >
                    {intl.formatMessage({
                      id: `fleetadmin.status.${license.licenseStatus}`,
                    })}
                  </Tag>
                )}
              </div>
            }
            key={index}
          >
            <ListEditItem
              testid="UserDL-status"
              label={intl.formatMessage({
                id: `fleetadmin.users.info.dl.status`,
              })}
              leftItem={renderText(license.licenseStatus, false)}
              onClickEdit={onDLStatusUpdate}
            />
            {license.licenseStatus === 'deleted' && (
              <ListEditItem
                testid="UserDL-rejectedReason"
                label={intl.formatMessage({
                  id: `fleetadmin.users.info.dl.rejectedReason`,
                })}
                leftItem={renderText(license.rejectedReason, true)}
              />
            )}
            <ListItem
              testid="UserDL-firstName"
              label={intl.formatMessage({
                id: `fleetadmin.users.info.dl.firstName`,
              })}
              value={renderText(license.firstName, false)}
            />
            <ListItem
              testid="UserDL-lastName"
              label={intl.formatMessage({
                id: `fleetadmin.users.info.dl.lastName`,
              })}
              value={renderText(license.lastName, false)}
            />
            <ListItem
              testid="UserDL-DLnum"
              label={intl.formatMessage({
                id: `fleetadmin.users.info.dl.number`,
              })}
              value={renderText(license.number, false)}
            />
            <ListItem
              testid="UserDL-classBFrom"
              label={intl.formatMessage({
                id: `fleetadmin.users.info.dl.classBFrom`,
              })}
              value={renderDate(license?.classB?.from, true)}
            />
            <ListItem
              testid="UserDL-classBTo"
              label={intl.formatMessage({
                id: `fleetadmin.users.info.dl.classBTo`,
              })}
              value={renderDate(license?.classB?.to, true)}
            />
            <ListItem
              testid="UserDL-issueFrom"
              label={intl.formatMessage({
                id: `fleetadmin.users.info.dl.issueFrom`,
              })}
              value={renderDate(license?.interval?.from, true)}
            />
            <ListItem
              testid="UserDL-issueAuthority"
              label={intl.formatMessage({
                id: `fleetadmin.users.info.dl.issuingAuthority`,
              })}
              value={renderText(license?.issuingAuthority, false)}
            />
            <ListItem
              testid="UserDL-dateOfBirth"
              label={intl.formatMessage({
                id: `fleetadmin.users.info.dl.dateOfBirth`,
              })}
              value={renderDate(license?.dateOfBirth, true)}
            />
            <ListItem
              testid="UserDL-issueCountry"
              label={intl.formatMessage({
                id: `fleetadmin.users.info.dl.issueCountry`,
              })}
              value={renderText(
                intl.formatMessage({
                  id: `${license?.issueCountry}`,
                }),
                false
              )}
            />
            <ListItem
              testid="UserDL-lastchangedat"
              label={intl.formatMessage({
                id: `fleetadmin.users.info.dl.lastchangedat`,
              })}
              value={renderDate(license?.lastChangedAt, true)}
            />
            <ListItem
              testid="UserDL-lastchangedby"
              label={intl.formatMessage({
                id: `fleetadmin.users.info.dl.lastchangedby`,
              })}
              value={renderText(license?.lastChangedBy, true)}
            />
            {license?.frontImageUrl && (
              <div className="dl-image-container">
                <ImageAdded
                  vehicleEvent={{
                    images: [
                      license?.frontImageUrl,
                      license?.backImageUrl,
                      license?.selfieUrl,
                    ],
                  }}
                />
              </div>
            )}
          </Panel>
        ))}
      </Collapse>
    </div>
    <style jsx>{styles}</style>
  </div>
);

export default UserDLInfo;
